import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@heroui/react';
import { countParam } from '@/pages/catalogImport/notices';
import type { SuggestedFix, SuggestedFixKind } from '../../suggestedFixes';

/**
 * Each line of "What we changed" (`dialog.fixes.<kind>`, plural, and its
 * `<kind>How`), as literal keys so the locale guards see every one.
 */
const FIX_COPY = {
    imei: {
        title: 'catalogImport.products.suggestedFixes.dialog.fixes.imei',
        how: 'catalogImport.products.suggestedFixes.dialog.fixes.imeiHow',
    },
    variantFields: {
        title: 'catalogImport.products.suggestedFixes.dialog.fixes.variantFields',
        how: 'catalogImport.products.suggestedFixes.dialog.fixes.variantFieldsHow',
    },
    groupFields: {
        title: 'catalogImport.products.suggestedFixes.dialog.fixes.groupFields',
        how: 'catalogImport.products.suggestedFixes.dialog.fixes.groupFieldsHow',
    },
    sharedBarcode: {
        title: 'catalogImport.products.suggestedFixes.dialog.fixes.sharedBarcode',
        how: 'catalogImport.products.suggestedFixes.dialog.fixes.sharedBarcodeHow',
    },
    alternates: {
        title: 'catalogImport.products.suggestedFixes.dialog.fixes.alternates',
        how: 'catalogImport.products.suggestedFixes.dialog.fixes.alternatesHow',
    },
} as const satisfies Record<SuggestedFixKind, { title: string; how: string }>;

/** One fix of the summary, as `suggestedFixesSummaryOf` lists it. */
type SuggestedFixLineProps = SuggestedFix;

/**
 * One line of "What we changed": the kind's count, the rows its default
 * changed ("112 rows changed", none for the extra barcodes) and how.
 */
export const SuggestedFixLine: React.FC<SuggestedFixLineProps> = ({
    kind,
    count,
    changedRows,
}) => {
    const { t } = useTranslation('pages');
    return (
        <div className="flex flex-col gap-0.5">
            <Typography type="body-sm" weight="medium">
                {t(FIX_COPY[kind].title, { count, rows: countParam(count) })}
            </Typography>
            {changedRows === null ? null : (
                <Typography type="body-xs">
                    {t(
                        'catalogImport.products.suggestedFixes.dialog.rowsChanged',
                        { count: changedRows, rows: countParam(changedRows) },
                    )}
                </Typography>
            )}
            <Typography color="muted" type="body-xs">
                {t(FIX_COPY[kind].how)}
            </Typography>
        </div>
    );
};
